import React, { useCallback, useContext, useEffect } from 'react';

import GlobalAlert from '../shared/components/global-alerts/global-alert/global-alert';
import { UiStateContext } from '../shared/contexts/ui-state/UiStateContext';
import useFeatureToggle from '../shared/hooks/use-feature-toggle/useFeatureToggle';
import { FeatureToggle, IconPack } from '../shared/models/app-data-model';

export type UserLocationPromptProps = {
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
};

/**
 * This Component prompts the user if they want to share their location with us
 * BEFORE we let the browser prompt them for location.
 */
const UserLocationPrompt: React.FC<UserLocationPromptProps> = ({ featureToggles, iconPack }: UserLocationPromptProps) => {
  const locationServicesEnabled = useFeatureToggle('location-services', featureToggles);
  const globalToolbarLocationEnabled = useFeatureToggle('global-toolbar-location', featureToggles);
  const featureIsEnabled = locationServicesEnabled && !globalToolbarLocationEnabled;
  const uiStateContext = useContext(UiStateContext);
  const isBrowser = typeof window !== 'undefined';
  /* istanbul ignore else */
  const userLocationPrefInStorage = isBrowser ? window?.localStorage?.getItem('location-services') : null;

  useEffect(() => {
    if (userLocationPrefInStorage) {
      uiStateContext?.setUserAcceptedLocationPrompt?.(userLocationPrefInStorage === 'granted');
      uiStateContext?.setUserDeniedLocationPrompt?.(userLocationPrefInStorage === 'denied');
    }
  });

  const acceptCallback = useCallback(() => {
    window?.localStorage?.setItem('location-services', 'granted');
    uiStateContext.setUserDeniedLocationPrompt?.(false);
    uiStateContext.setUserAcceptedLocationPrompt?.(true);
    /* istanbul ignore next */
    navigator.geolocation.getCurrentPosition((position) => {
      /* istanbul ignore next */
      if (position?.coords?.latitude && position?.coords?.longitude && uiStateContext.setBrowserLocation) {
        uiStateContext.setBrowserLocation(position.coords);
      }
    });
  }, [uiStateContext]);

  const denyCallback = useCallback(() => {
    window?.localStorage?.setItem('location-services', 'denied');
    uiStateContext.setUserAcceptedLocationPrompt?.(false);
    uiStateContext.setUserDeniedLocationPrompt?.(true);
    /* istanbul ignore if  */
    if (uiStateContext.setBrowserLocation) {
      uiStateContext.setBrowserLocation({ latitude: 0, longitude: 0 });
    }
  }, [uiStateContext]);

  return uiStateContext?.userAcceptedLocationPrompt || uiStateContext?.userDeniedLocationPrompt || !featureIsEnabled ? null : (
    <GlobalAlert
      iconPack={iconPack}
      alert={{
        alert: {
          headline: 'Location Sharing',
          body: '<p>Enabling this provides you with more accurate distances to providers and locations</p>',
          type: 'Default'
        },
        primary_button: {
          text: 'Share',
          button_type: 'primary',
          onClick: acceptCallback
        },
        secondary_button: {
          text: 'Deny',
          button_type: 'secondary',
          onClick: denyCallback
        }
      }}
      closeAlertCallback={denyCallback}
    />
  );
};

export default UserLocationPrompt;
